import { Pie } from '@visx/shape'
import { Group } from '@visx/group'
import type { ChartResult, ChartSpec } from '../../../types/chart.js'
import { getSeriesStyle } from './chartTheme.js'

export interface DonutChartProps {
  spec: ChartSpec
  data: ChartResult
  width?: number
  height?: number
  className?: string
  testId?: string
}

interface PieSliceData {
  label: string
  value: number
  index: number
}

export function DonutChart({
  spec,
  data,
  width = 500,
  height = 240,
  className = '',
  testId,
}: DonutChartProps) {
  const radius = Math.min(width, height) / 2 - 20
  const centerX = width / 2
  const centerY = height / 2

  // Inner radius: 0 = full pie, default = donut (0.6 * radius)
  const innerRadius = typeof spec.style?.innerRadius === 'number'
    ? spec.style.innerRadius
    : radius * 0.6

  // Flatten points or series into slices
  const slices: PieSliceData[] = data.series.flatMap((series, sIdx) => {
    if (series.points.length > 1) {
      return series.points.map((p, pIdx) => ({
        label: `${series.label} - ${String(p.x)}`,
        value: Math.max(0, p.y),
        index: sIdx + pIdx,
      }))
    }
    const val = series.points[0]?.y ?? 0
    return [{
      label: series.label,
      value: Math.max(0, val),
      index: sIdx,
    }]
  })

  return (
    <svg
      width={width}
      height={height}
      className={`chartSvg ${className}`}
      data-testid={testId}
      role="img"
      aria-label={spec.title || 'Donut Chart'}
    >
      <Group top={centerY} left={centerX}>
        <Pie<PieSliceData>
          data={slices}
          pieValue={(d) => d.value}
          outerRadius={radius}
          innerRadius={innerRadius}
          padAngle={0.02}
        >
          {(pie) =>
            pie.arcs.map((arc, idx) => {
              const style = getSeriesStyle(arc.data.index)
              return (
                <path
                  key={idx}
                  d={pie.path(arc) || ''}
                  fill={style.color}
                  stroke={style.isDashed ? 'var(--surface)' : undefined}
                  strokeDasharray={style.strokeDasharray}
                />
              )
            })
          }
        </Pie>
      </Group>
    </svg>
  )
}
